import { checkReadyToBid } from '@/components/GridItemCard/Card/helpers/checkReadyToBid';
import { getBidLimitBalance } from '@/redux/modules/bidLimit';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
} from '@/redux/modules/console';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemIndex,
    getItemModelCatalogId,
    getItemModelCatalogStatus,
    getItemModelCurrency,
    getItemModelSellerId,
    getItemModelStartPrice,
    getItemModelTitle,
    getItemSummary,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { isSavedItemsPage } from '@/utils/urls';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { ItemListNames, trackProductClicked } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import CardGridBody from './components/CardGridBody/CardGridBody';
import CardImage from './components/CardImage/CardImage';
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const TimedPlusMyItemsCard: ItemCardComponent = ({ className, itemId }) => {
    const dispatch = useAppDispatch();

    const { pathname } = useLocation();

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);

    const { leadingBid, salePrice, startPrice } = useAppSelector((state) => getItemSummary(state, itemId));

    const index = useAppSelector((state) => getItemIndex(state, itemId));
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemModelCurrency(state, itemId));
    const houseId = useAppSelector((state) => getItemModelSellerId(state, itemId));
    const price = useAppSelector((state) => getItemModelStartPrice(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemModelCatalogStatus(state, itemId));
    const title = useAppSelector((state) => getItemModelTitle(state, itemId));

    const [hover, setHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const isDone = catalogStatus === 'done';
    const currentItemLive = comingUp && currentItemIndex === index;

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid({
            catalogId,
            closed,
            currentAskPrice,
            currentItem,
            itemId,
            liveUpdateTimestamp,
            networkStatus,
            status,
            won,
        });

    const onCardClick = useCallback(() => {
        if (isSavedItemsPage(pathname)) {
            dispatch(
                trackProductClicked({
                    catalogId,
                    catalogStatus,
                    currency,
                    houseId,
                    itemId,
                    itemListName: ItemListNames.SavedItems,
                    price,
                    title,
                })
            );
        }
    }, [catalogId, catalogStatus, currency, dispatch, houseId, itemId, pathname, price, title]);

    const enterHoverState = React.useCallback(() => {
        setHover(true);
    }, []);

    const leaveHoverState = React.useCallback(() => {
        setHover(false);
    }, []);

    const enterLinkHoverState = React.useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = React.useCallback(() => {
        setLinkHover(false);
    }, []);

    return (
        <StyledCard
            data-testid={`timed-plus-item-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <SavedItemsCardGrid
                $auctionEnded={isDone}
                className={className}
                data-testid="saved-items-card-grid"
            >
                <CardImage
                    enterLinkHoverState={enterLinkHoverState}
                    isCatalogDone={isDone}
                    isMobile={isMobile}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                />
                <CardGridBody
                    cardHovered={hover}
                    enterHoverState={enterHoverState}
                    enterLinkHoverState={enterLinkHoverState}
                    isCurrentLiveitem={currentItemLive}
                    isMobile={isMobile}
                    isStoppedFromBidding={isStoppedFromBidding}
                    itemId={itemId}
                    leaveHoverState={leaveHoverState}
                    leaveLinkHoverState={leaveLinkHoverState}
                    linkHovered={linkHover}
                />
            </SavedItemsCardGrid>
        </StyledCard>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: TimedPlusMyItemsCard,
};

export default info;

const StyledCard = styled.article`
    position: relative;
`;

const SavedItemsCardGrid = styled.section<{
    $auctionEnded: boolean;
}>`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content min-content;
    grid-template-areas:
        'itemimage'
        '.'
        'alldetailsformobilelayout';
    justify-content: center;

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-areas: 'itemimage alldetailsformobilelayout';
        grid-template-columns: 1fr 4fr;
        grid-template-rows: min-content min-content;
        column-gap: 16px;
        ${({ $auctionEnded }) =>
            $auctionEnded &&
            css`
                grid-template-rows: min-content 11px min-content 4px min-content 2px min-content 0;
            `}
    }
`;
